import Axios from 'axios';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import { Helmet } from 'react-helmet-async';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { Store } from '../Store';
import { getError } from '../utils';
import { api } from '../Api';
import { useOtpGate, OtpGateModal } from '../component/OtpGate';
import ConfirmActionModal from '../component/ConfirmActionModal';

const DEFAULT_LOGO =
  'https://res.cloudinary.com/dddogyqqa/image/upload/v1670435815/logo/clickcart-logo_wfktlg.png';

/**
 * TOPUP — the member's side.
 *
 * Ask for shopping balance against money paid outside the system, watch the
 * request move through the two checks, and (for a supervisor) pass balance
 * down to somebody in their own downline.
 *
 * Nothing here credits anything. The status line is the whole point: a member
 * who can see "waiting for the office to check your payment" does not ring to
 * ask. The frame — logo, blue card, back arrow in the header — is the same one
 * every other member screen uses, so this does not feel like a different site.
 */

const STATUS_STYLE = {
  pending: { bg: '#fef3c7', color: '#92400e', label: 'Waiting for Accounts' },
  verified: { bg: '#dbeafe', color: '#1d4ed8', label: 'Verified by Accounts, waiting for admin' },
  approved: { bg: '#dcfce7', color: '#166534', label: 'Approved' },
  rejected: { bg: '#fee2e2', color: '#991b1b', label: 'Rejected' },
  cancelled: { bg: '#e5e7eb', color: '#374151', label: 'Cancelled' },
};

const METHOD_ICON = {
  cash: 'fa-money',
  mobile_banking: 'fa-mobile',
  bank_transfer: 'fa-university',
  other: 'fa-ellipsis-h',
};

const LOGO =
  'https://res.cloudinary.com/dddogyqqa/image/upload/v1670435815/logo/clickcart-logo_wfktlg.png';

/**
 * The same shell every other member screen wears — logo, blue card, a header
 * that doubles as the back arrow — so this page does not look like a visitor.
 * It lives outside the screen so typing in a field does not remount the form.
 */
function Frame({ onBack, children }) {
  return (
    <div>
      <Helmet>
        <title>Topup Balance</title>
      </Helmet>
      <style>{CSS}</style>
      <Container className="no-gutter">
        <div className="card mb-3">
          <Row>
            <Col style={{ textAlign: 'center' }}>
              <img src={LOGO} style={{ width: '15rem', margin: '2rem' }} alt="ClickCart Logo" />
            </Col>
          </Row>
          <div className="card-header" onClick={onBack} role="button">
            <i className="fa fa-long-arrow-left" aria-hidden="true"></i> {'  '} Topup Balance
          </div>
          <div className="card-body tu-body">{children}</div>
        </div>
      </Container>
    </div>
  );
}

export default function TopupScreen() {
  const { state } = useContext(Store);
  const { userInfo } = state;
  const navigate = useNavigate();
  const auth = useMemo(
    () => ({ headers: { authorization: `Bearer ${userInfo?.token}` } }),
    [userInfo]
  );

  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState('');
  const [provider, setProvider] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [transactionId, setTransactionId] = useState('');
  const [branch, setBranch] = useState('');
  const [note, setNote] = useState('');

  const [history, setHistory] = useState({ rows: [], page: 1, pages: 1, count: 0 });
  const [open, setOpen] = useState('');
  const REQUEST_PAGE_SIZE = 50;
  const [reqSearch, setReqSearch] = useState('');
  const [reqStatus, setReqStatus] = useState('');
  const [reqMethod, setReqMethod] = useState('');
  const [reqFrom, setReqFrom] = useState('');
  const [reqTo, setReqTo] = useState('');
  const [reqFilters, setReqFilters] = useState({ q: '', status: '', method: '', from: '', to: '' });
  const reqFiltersActive =
    reqFilters.q || reqFilters.status || reqFilters.method || reqFilters.from || reqFilters.to;

  const loadSettings = useCallback(async () => {
    try {
      const { data } = await Axios.get(api + '/api/topup/settings', auth);
      setSettings(data);
      const firstEnabled = (data.methods || []).find((m) => m.enabled);
      setMethod((current) => current || firstEnabled?.key || '');
    } catch (error) {
      toast.error(getError(error));
    } finally {
      setLoading(false);
    }
  }, [auth]);

  const loadHistory = useCallback(
    async (page = 1, filters = reqFilters) => {
      try {
        const params = new URLSearchParams({ page, pageSize: REQUEST_PAGE_SIZE });
        if (filters.q) params.set('q', filters.q);
        if (filters.status) params.set('status', filters.status);
        if (filters.method) params.set('method', filters.method);
        if (filters.from) params.set('from', filters.from);
        if (filters.to) params.set('to', filters.to);
        const { data } = await Axios.get(
          api + `/api/topup/my?${params.toString()}`,
          auth
        );
        setHistory(data);
      } catch (error) {
        /* the list is informational — the form still works without it */
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [auth]
  );

  const applyReqFilters = () => {
    const next = { q: reqSearch.trim(), status: reqStatus, method: reqMethod, from: reqFrom, to: reqTo };
    setReqFilters(next);
    loadHistory(1, next);
  };

  const clearReqFilters = () => {
    setReqSearch('');
    setReqStatus('');
    setReqMethod('');
    setReqFrom('');
    setReqTo('');
    setReqFilters({ q: '', status: '', method: '', from: '', to: '' });
    loadHistory(1, { q: '', status: '', method: '', from: '', to: '' });
  };

  useEffect(() => {
    loadSettings();
    loadHistory(1, reqFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadSettings]);

  const chosen = (settings?.methods || []).find((m) => m.key === method);

  const submit = async () => {
    const value = Number(amount);
    if (!Number.isFinite(value) || value <= 0) {
      toast.error('Enter the amount you paid.');
      return;
    }
    if (!method) {
      toast.error('Choose how you paid.');
      return;
    }
    try {
      setBusy(true);
      const { data } = await Axios.post(
        api + '/api/topup/request',
        {
          amountBdt: value,
          method,
          methodDetails: { provider, accountNumber, transactionId, branch },
          note,
        },
        auth
      );
      toast.success(data.message);
      setAmount('');
      setTransactionId('');
      setAccountNumber('');
      setProvider('');
      setBranch('');
      setNote('');
      loadHistory(1, reqFilters);
      loadSettings();
    } catch (error) {
      toast.error(getError(error));
    } finally {
      setBusy(false);
    }
  };

  const cancel = async (row) => {
    if (!window.confirm(`Cancel your request for BDT ${row.amountBdt}?`)) return;
    try {
      await Axios.post(api + `/api/topup/my/${row._id}/cancel`, {}, auth);
      toast.success('Request cancelled.');
      loadHistory(history.page, reqFilters);
    } catch (error) {
      toast.error(getError(error));
    }
  };

  /* ------------------------------------------------------- supervisor share */

  const [shareTo, setShareTo] = useState('');
  const [shareAmount, setShareAmount] = useState('');
  const [shareNote, setShareNote] = useState('');
  const [shareCheck, setShareCheck] = useState(null);
  const [shares, setShares] = useState([]);

  const isSupervisor = Boolean(settings?.me?.isSuperVisor);

  const loadShares = useCallback(async () => {
    if (!isSupervisor) return;
    try {
      const { data } = await Axios.get(api + '/api/topup/shares?pageSize=10', auth);
      setShares(data.rows || []);
    } catch (error) {
      /* informational */
    }
  }, [auth, isSupervisor]);

  useEffect(() => {
    loadShares();
  }, [loadShares]);

  const checkReceiver = async () => {
    const username = shareTo.trim();
    if (!username) return;
    try {
      const { data } = await Axios.get(
        api + `/api/topup/share/check?username=${encodeURIComponent(username)}`,
        auth
      );
      setShareCheck(data);
    } catch (error) {
      setShareCheck({ allowed: false, reason: getError(error) });
    }
  };

  // The confirm modal for the supervisor share — holds what was reviewed, so
  // the POST sends exactly that and not whatever the fields say by then.
  const [shareConfirm, setShareConfirm] = useState(null);

  const share = async () => {
    const amount = Number(shareAmount);
    const toUsername = shareTo.trim();
    if (!toUsername || !Number.isFinite(amount) || amount <= 0) {
      toast.error('Enter a username and the amount (Tk) to send.');
      return;
    }
    // Look the receiver up first so the modal can name them.
    let check = shareCheck;
    if (!check || check.receiver?.username !== toUsername) {
      try {
        const { data } = await Axios.get(
          api + `/api/topup/share/check?username=${encodeURIComponent(toUsername)}`,
          auth
        );
        check = data;
      } catch (error) {
        check = { allowed: false, reason: getError(error) };
      }
      setShareCheck(check);
    }
    if (!check?.allowed) {
      toast.error(check?.reason || 'That member is not in your downline.');
      return;
    }
    setShareConfirm({ toUsername, amount, note: shareNote, receiver: check.receiver });
  };

  const doShare = async ({ toUsername, amount, note: shareNoteValue }) => {
    try {
      setBusy(true);
      const { data } = await Axios.post(
        api + '/api/topup/share',
        { toUsername, amount, note: shareNoteValue },
        auth
      );
      toast.success(data.message);
      setShareConfirm(null);
      setShareTo('');
      setShareAmount('');
      setShareNote('');
      setShareCheck(null);
      loadSettings();
      loadShares();
    } catch (error) {
      toast.error(getError(error));
    } finally {
      setBusy(false);
    }
  };

  /* ----------------------------------------------------- association transfer */

  const isAssociation = Boolean(settings?.me?.isAssociation);
  const [assocQuery, setAssocQuery] = useState('');
  const [assocResults, setAssocResults] = useState([]);
  const [assocSearching, setAssocSearching] = useState(false);
  const [assocSelected, setAssocSelected] = useState(null);
  const [assocAmount, setAssocAmount] = useState('');
  const [assocNote, setAssocNote] = useState('');
  const [assocPassword, setAssocPassword] = useState('');
  const [assocHistory, setAssocHistory] = useState({ rows: [], page: 1, pages: 1, count: 0 });
  const [assocOpen, setAssocOpen] = useState('');
  const [historyTab, setHistoryTab] = useState('requests');

  const [xferSearch, setXferSearch] = useState('');
  const [xferFrom, setXferFrom] = useState('');
  const [xferTo, setXferTo] = useState('');
  const [xferFilters, setXferFilters] = useState({ q: '', from: '', to: '' });
  const xferFiltersActive = xferFilters.q || xferFilters.from || xferFilters.to;

  // Fetched for every member, not just merchants — anyone in a merchant's
  // downline can be on the receiving end of a transfer, and needs to see it
  // too. The tab itself only appears once there is something to show.
  const loadAssocTransfers = useCallback(
    async (page = 1, filters = xferFilters) => {
      try {
        const params = new URLSearchParams({ page, pageSize: 10 });
        if (filters.q) params.set('q', filters.q);
        if (filters.from) params.set('from', filters.from);
        if (filters.to) params.set('to', filters.to);
        const { data } = await Axios.get(
          api + `/api/association/transfers/mine?${params.toString()}`,
          auth
        );
        setAssocHistory(data);
      } catch (error) {
        /* informational */
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [auth]
  );

  const applyXferFilters = () => {
    const next = { q: xferSearch.trim(), from: xferFrom, to: xferTo };
    setXferFilters(next);
    loadAssocTransfers(1, next);
  };

  const clearXferFilters = () => {
    setXferSearch('');
    setXferFrom('');
    setXferTo('');
    setXferFilters({ q: '', from: '', to: '' });
    loadAssocTransfers(1, { q: '', from: '', to: '' });
  };

  useEffect(() => {
    loadAssocTransfers(1, xferFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isAssociation || assocQuery.trim().length < 2) {
      setAssocResults([]);
      return undefined;
    }
    let active = true;
    setAssocSearching(true);
    const timer = setTimeout(async () => {
      try {
        const { data } = await Axios.get(
          api + `/api/association/downline/search?q=${encodeURIComponent(assocQuery.trim())}`,
          auth
        );
        if (active) setAssocResults(data.rows || []);
      } catch (error) {
        if (active) setAssocResults([]);
      } finally {
        if (active) setAssocSearching(false);
      }
    }, 350);
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [assocQuery, isAssociation, auth]);

  const assocOtpGate = useOtpGate({
    path: 'association/transfer',
    token: userInfo?.token,
    onSuccess: (data) => {
      toast.success(data?.message || 'Transfer sent.');
      setAssocSelected(null);
      setAssocQuery('');
      setAssocAmount('');
      setAssocNote('');
      setAssocPassword('');
      loadSettings();
      loadAssocTransfers(1, xferFilters);
    },
    onError: (error) => {
      toast.error(getError(error));
    },
  });

  // The confirm modal for a merchant transfer. It comes BEFORE the OTP gate:
  // review, Confirm, and only then is a code (if the gate is on) sent.
  const [assocConfirm, setAssocConfirm] = useState(null);

  const sendAssociationTransfer = async () => {
    const points = Number(assocAmount);
    if (!assocSelected) {
      toast.error('Search and select who you are sending to.');
      return;
    }
    if (!Number.isFinite(points) || points <= 0) {
      toast.error('Enter how much to send.');
      return;
    }
    if (!assocPassword) {
      toast.error('Enter your password to confirm.');
      return;
    }
    if (points > Number(settings?.me?.shoppingbalance || 0)) {
      toast.error('You do not have that much shopping balance.');
      return;
    }
    setAssocConfirm({ receiver: assocSelected, amount: points, note: assocNote });
  };

  const doAssociationTransfer = async ({ receiver, amount, note: noteValue }) => {
    await assocOtpGate.start({
      toUsername: receiver.username,
      amount,
      note: noteValue,
      password: assocPassword,
    });
    // Either it went through (onSuccess reset the form) or a code was sent and
    // the OTP modal has taken over; on an error the toast says why. In every
    // case this review step is done.
    setAssocConfirm(null);
  };

  const goBack = () => navigate('/user-panel');

  if (loading) {
    return (
      <Frame onBack={goBack}>
        <div className="tu-panel tu-center tu-muted">Loading…</div>
      </Frame>
    );
  }

  if (settings && !settings.enabled) {
    return (
      <Frame onBack={goBack}>
        <div className="tu-panel tu-center">
          <div className="tu-empty-icon">
            <i className="fa fa-pause-circle" aria-hidden="true"></i>
          </div>
          <b>Topups are switched off at the moment.</b>
          <div className="tu-muted">Please try again later, or ask the office.</div>
        </div>
      </Frame>
    );
  }

  // Topups are BDT only: the Shopping Balance goes up by the amount paid.
  const willGet = Number(amount) > 0 ? Math.round(Number(amount)) : 0;

  return (
    <Frame onBack={goBack}>
      <OtpGateModal
        gate={assocOtpGate}
        title="Confirm this transfer"
        actionLabel="Confirm transfer"
      />
      {shareConfirm ? (() => {
        const balance = Number(settings?.me?.shoppingbalance || 0);
        const r = shareConfirm.receiver || {};
        return (
          <ConfirmActionModal
            show
            title="Send balance to downline"
            recipient={{ name: r.name, username: r.username || shareConfirm.toUsername, photo: r.profilePhoto }}
            amount={`Tk ${shareConfirm.amount.toLocaleString()}`}
            amountHint="From your Shopping Balance"
            rows={[
              { label: 'To', value: `${r.name || shareConfirm.toUsername} (@${r.username || shareConfirm.toUsername})` },
              { label: 'Amount', value: `Tk ${shareConfirm.amount.toLocaleString()}` },
              ...(shareConfirm.note ? [{ label: 'Note', value: shareConfirm.note }] : []),
              { label: 'Your Shopping Balance now', value: `Tk ${balance.toLocaleString()}` },
              { label: 'Your Shopping Balance after', value: `Tk ${(balance - shareConfirm.amount).toLocaleString()}`, strong: true, tone: balance - shareConfirm.amount < 0 ? 'bad' : undefined },
            ]}
            note={`Tk ${shareConfirm.amount.toLocaleString()} leaves your Shopping Balance immediately and is added to @${r.username || shareConfirm.toUsername}'s Shopping Balance.`}
            confirmLabel="Send"
            busyLabel="Sending…"
            busy={busy}
            onCancel={() => setShareConfirm(null)}
            onConfirm={() => doShare(shareConfirm)}
          />
        );
      })() : null}
      {assocConfirm ? (() => {
        const balance = Number(settings?.me?.shoppingbalance || 0);
        const r = assocConfirm.receiver;
        return (
          <ConfirmActionModal
            show
            title="Merchant transfer to downline"
            recipient={{ name: r.name, username: r.username, photo: r.profilePhoto }}
            amount={`Tk ${assocConfirm.amount.toLocaleString()}`}
            amountHint={`From your Shopping Balance, as ${settings?.me?.associationName || 'Merchant'}`}
            rows={[
              { label: 'To', value: `${r.name || r.username} (@${r.username})` },
              { label: 'Amount', value: `Tk ${assocConfirm.amount.toLocaleString()}` },
              ...(assocConfirm.note ? [{ label: 'Note', value: assocConfirm.note }] : []),
              { label: 'Your Shopping Balance now', value: `Tk ${balance.toLocaleString()}` },
              { label: 'Your Shopping Balance after', value: `Tk ${(balance - assocConfirm.amount).toLocaleString()}`, strong: true },
            ]}
            note={
              `Tk ${assocConfirm.amount.toLocaleString()} leaves your Shopping Balance immediately and is added to ` +
              `@${r.username}'s Shopping Balance. If a verification code is required, it is sent after you confirm.`
            }
            confirmLabel="Confirm transfer"
            busyLabel="Sending…"
            busy={assocOtpGate.busy}
            onCancel={() => setAssocConfirm(null)}
            onConfirm={() => doAssociationTransfer(assocConfirm)}
          />
        );
      })() : null}
      {/* ---------------------------------------------------- balance banner */}
      <div className="tu-hero">
        <div className="tu-hero-main">
          <span>Shopping balance</span>
          <strong>৳{Number(settings?.me?.shoppingbalance || 0).toLocaleString()}</strong>
        </div>
        {isSupervisor ? (
          <span className="tu-chip tu-chip-green">
            <i className="fa fa-star" aria-hidden="true"></i> Supervisor
          </span>
        ) : null}
        {isAssociation ? (
          <span className="tu-chip tu-chip-purple">
            <img
              src={settings?.me?.associationLogo || DEFAULT_LOGO}
              alt=""
              className="tu-assoc-logo"
            />
            {settings?.me?.associationName || 'Merchant'}
          </span>
        ) : null}
      </div>

      <p className="tu-intro">
        Pay by cash, mobile banking or bank transfer, then tell us about it below.
        Our Accounts team verifies the payment first, then an admin approves it and
        the same BDT amount is added to your Shopping Balance.
      </p>

      <div className="tu-grid">
        {/* ------------------------------------------------------- the form */}
        <section className="tu-panel">
          <h6 className="tu-panel-head">
            <i className="fa fa-plus-circle" aria-hidden="true"></i> New request
          </h6>

          <label className="tu-label">Amount you paid (BDT)</label>
          <input
            className="tu-input tu-amount"
            type="number"
            inputMode="numeric"
            min={settings?.minAmount}
            max={settings?.maxAmount}
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder={`${Number(settings?.minAmount || 0).toLocaleString()} – ${Number(
              settings?.maxAmount || 0
            ).toLocaleString()}`}
          />
          {willGet > 0 ? (
            <div className="tu-preview">
              <strong>৳{willGet.toLocaleString()}</strong> will be added to your Shopping
              Balance once this is approved.
            </div>
          ) : null}

          <label className="tu-label">How did you pay?</label>
          <div className="tu-methods">
            {(settings?.methods || [])
              .filter((m) => m.enabled)
              .map((m) => (
                <button
                  key={m.key}
                  type="button"
                  className={m.key === method ? 'tu-method tu-method-on' : 'tu-method'}
                  onClick={() => setMethod(m.key)}
                >
                  <i className={`fa ${METHOD_ICON[m.key] || 'fa-credit-card'}`} aria-hidden="true"></i>
                  <span>{m.label}</span>
                </button>
              ))}
          </div>
          {chosen?.instructions ? (
            <div className="tu-hint">
              <i className="fa fa-info-circle" aria-hidden="true"></i> {chosen.instructions}
            </div>
          ) : null}

          {method === 'mobile_banking' || method === 'bank_transfer' ? (
            <div className="tu-two">
              <div>
                <label className="tu-label">
                  {method === 'bank_transfer' ? 'Bank name' : 'Service (bKash, Nagad, Rocket)'}{' '}
                  <small className="text-muted">(optional)</small>
                </label>
                <input
                  className="tu-input"
                  value={provider}
                  onChange={(e) => setProvider(e.target.value)}
                />
              </div>
              <div>
                <label className="tu-label">
                  The account you sent from <small className="text-muted">(optional)</small>
                </label>
                <input
                  className="tu-input"
                  value={accountNumber}
                  onChange={(e) => setAccountNumber(e.target.value)}
                />
              </div>
            </div>
          ) : null}

          <div className="tu-two">
            {method === 'bank_transfer' ? (
              <div>
                <label className="tu-label">
                  Branch <small className="text-muted">(optional)</small>
                </label>
                <input
                  className="tu-input"
                  value={branch}
                  onChange={(e) => setBranch(e.target.value)}
                />
              </div>
            ) : null}
            {chosen?.requireTransactionId || method === 'mobile_banking' || method === 'bank_transfer' ? (
              <div>
                <label className="tu-label">
                  Transaction id
                  {method === 'mobile_banking' || method === 'bank_transfer' ? (
                    <>
                      {' '}
                      <small className="text-muted">(optional)</small>
                    </>
                  ) : null}
                </label>
                <input
                  className="tu-input"
                  value={transactionId}
                  onChange={(e) => setTransactionId(e.target.value)}
                  placeholder="From your payment receipt"
                />
              </div>
            ) : null}
          </div>

          <label className="tu-label">Note (optional)</label>
          <textarea
            className="tu-input"
            rows={2}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Anything our team should know"
          />

          <button className="tu-btn tu-btn-primary tu-full" onClick={submit} disabled={busy}>
            {busy ? 'Sending…' : 'Send this request'}
          </button>
        </section>

        {/* ------------------------------------------------- supervisor share */}
        {isSupervisor && settings?.sharing?.enabled ? (
          <section className="tu-panel">
            <h6 className="tu-panel-head">
              <i className="fa fa-share-alt" aria-hidden="true"></i> Share balance with my downline
            </h6>
            <div className="tu-hint">
              <i className="fa fa-info-circle" aria-hidden="true"></i> As a supervisor you can pass
              your own shopping balance to anyone
              {settings.sharing.wholeDownline
                ? ' in your downline'
                : ' placed directly under you'}
              . It leaves your balance immediately.
            </div>

            <label className="tu-label">Send to (username)</label>
            <div className="tu-row">
              <input
                className="tu-input"
                value={shareTo}
                onChange={(e) => {
                  setShareTo(e.target.value);
                  setShareCheck(null);
                }}
                onBlur={checkReceiver}
                placeholder="username"
              />
              <button className="tu-btn tu-btn-light" type="button" onClick={checkReceiver}>
                Check
              </button>
            </div>
            {shareCheck ? (
              <div className={shareCheck.allowed ? 'tu-ok' : 'tu-bad'}>
                <i
                  className={shareCheck.allowed ? 'fa fa-check-circle' : 'fa fa-times-circle'}
                  aria-hidden="true"
                ></i>{' '}
                {shareCheck.allowed
                  ? `${shareCheck.receiver.name} (${shareCheck.receiver.username}) is in your downline.`
                  : shareCheck.reason}
              </div>
            ) : null}

            <div className="tu-two">
              <div>
                <label className="tu-label">Amount to send (Tk)</label>
                <input
                  className="tu-input"
                  type="number"
                  inputMode="numeric"
                  value={shareAmount}
                  onChange={(e) => setShareAmount(e.target.value)}
                />
              </div>
              <div>
                <label className="tu-label">Note (optional)</label>
                <input
                  className="tu-input"
                  value={shareNote}
                  onChange={(e) => setShareNote(e.target.value)}
                />
              </div>
            </div>

            <button
              className="tu-btn tu-btn-primary tu-full"
              onClick={share}
              disabled={busy || (shareCheck && !shareCheck.allowed)}
            >
              Send balance
            </button>

            {shares.length ? (
              <>
                <div className="tu-sub">Recent transfers</div>
                <ul className="tu-list">
                  {shares.map((s) => {
                    const sent = s.fromUsername === userInfo?.username;
                    return (
                      <li key={s._id}>
                        <span className={sent ? 'tu-dir tu-dir-out' : 'tu-dir tu-dir-in'}>
                          <i
                            className={sent ? 'fa fa-arrow-up' : 'fa fa-arrow-down'}
                            aria-hidden="true"
                          ></i>{' '}
                          {sent ? `to ${s.toUsername}` : `from ${s.fromUsername}`}
                        </span>
                        <b>৳{Number(s.points).toLocaleString()}</b>
                        <small className="tu-muted">
                          {new Date(s.createdAt).toLocaleString()}
                        </small>
                      </li>
                    );
                  })}
                </ul>
              </>
            ) : null}
          </section>
        ) : null}

        {/* ------------------------------------------------ association transfer */}
        {isAssociation ? (
          <section className="tu-panel">
            <h6 className="tu-panel-head">
              <i className="fa fa-handshake-o" aria-hidden="true"></i> Send to my downline
            </h6>
            <div className="tu-hint">
              <i className="fa fa-info-circle" aria-hidden="true"></i> As a merchant you can send
              your shopping balance to anyone in your own downline. It leaves your balance immediately.
            </div>
            {settings?.me?.associationDisabled ? (
              <div className="tu-bad">
                <i className="fa fa-ban" aria-hidden="true"></i> Your merchant account is disabled by
                the office, so transfers are blocked until it is enabled again.
              </div>
            ) : null}

            {assocSelected ? (
              <div className="tu-assoc-picked">
                <img src={assocSelected.profilePhoto || DEFAULT_LOGO} alt="" className="tu-assoc-avatar" />
                <div className="flex-grow-1">
                  <div>
                    <b>{assocSelected.name}</b>
                  </div>
                  <small className="tu-muted">@{assocSelected.username}</small>
                </div>
                <button
                  type="button"
                  className="tu-btn tu-btn-light"
                  onClick={() => {
                    setAssocSelected(null);
                    setAssocQuery('');
                  }}
                >
                  Change
                </button>
              </div>
            ) : (
              <>
                <label className="tu-label">Search downline (username or name)</label>
                <input
                  className="tu-input"
                  value={assocQuery}
                  onChange={(e) => setAssocQuery(e.target.value)}
                  placeholder="Start typing…"
                />
                {assocSearching ? <div className="tu-muted small">Searching…</div> : null}
                {assocResults.length > 0 ? (
                  <div className="tu-suggest">
                    {assocResults.map((r) => (
                      <button
                        type="button"
                        key={r._id}
                        className="tu-suggest-row"
                        onClick={() => {
                          setAssocSelected(r);
                          setAssocResults([]);
                        }}
                      >
                        <img src={r.profilePhoto || DEFAULT_LOGO} alt="" className="tu-assoc-avatar" />
                        <div className="text-start">
                          <div>
                            <b>{r.name}</b>
                          </div>
                          <small className="tu-muted">@{r.username}</small>
                        </div>
                      </button>
                    ))}
                  </div>
                ) : null}
              </>
            )}

            <div className="tu-two">
              <div>
                <label className="tu-label">Amount to send (Tk)</label>
                <input
                  className="tu-input"
                  type="number"
                  inputMode="numeric"
                  value={assocAmount}
                  onChange={(e) => setAssocAmount(e.target.value)}
                />
              </div>
              <div>
                <label className="tu-label">Note (optional)</label>
                <input
                  className="tu-input"
                  value={assocNote}
                  onChange={(e) => setAssocNote(e.target.value)}
                />
              </div>
            </div>
            <label className="tu-label">Your password</label>
            <input
              className="tu-input"
              type="password"
              value={assocPassword}
              onChange={(e) => setAssocPassword(e.target.value)}
            />

            <button
              className="tu-btn tu-btn-primary tu-full"
              onClick={sendAssociationTransfer}
              disabled={assocOtpGate.busy}
            >
              {assocOtpGate.busy ? 'Sending…' : 'Send balance'}
            </button>

            <div className="tu-hint" style={{ marginTop: 4 }}>
              <i className="fa fa-arrow-down" aria-hidden="true"></i> Every transfer you send shows up
              in the <b>Merchant Transfers</b> tab below.
            </div>
          </section>
        ) : null}
      </div>

      {/* --------------------------------------------------------- my requests */}
      <section className="tu-panel">
        <h6 className="tu-panel-head">
          <i className="fa fa-history" aria-hidden="true"></i> My history
        </h6>

        {isAssociation || assocHistory.count > 0 ? (
          <div className="tu-tabs">
            <button
              type="button"
              className={historyTab === 'requests' ? 'tu-tab tu-tab-blue tu-tab-active' : 'tu-tab tu-tab-blue'}
              onClick={() => setHistoryTab('requests')}
            >
              <i className="fa fa-credit-card" aria-hidden="true"></i> Topup Requests
              <span className="tu-count">{history.count}</span>
            </button>
            <button
              type="button"
              className={historyTab === 'transfers' ? 'tu-tab tu-tab-purple tu-tab-active' : 'tu-tab tu-tab-purple'}
              onClick={() => setHistoryTab('transfers')}
            >
              <i className="fa fa-exchange" aria-hidden="true"></i> Merchant Transfers
              <span className="tu-count">{assocHistory.count}</span>
            </button>
          </div>
        ) : null}

        {historyTab === 'requests' ? (
          <div className="tu-filters">
            <input
              className="tu-input"
              placeholder="Search note or transaction id…"
              value={reqSearch}
              onChange={(e) => setReqSearch(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && applyReqFilters()}
            />
            <select className="tu-input" value={reqStatus} onChange={(e) => setReqStatus(e.target.value)}>
              <option value="">Any status</option>
              {Object.keys(STATUS_STYLE).map((s) => (
                <option value={s} key={s}>{s[0].toUpperCase() + s.slice(1)}</option>
              ))}
            </select>
            <select className="tu-input" value={reqMethod} onChange={(e) => setReqMethod(e.target.value)}>
              <option value="">Any method</option>
              {(settings?.methods || []).map((m) => (
                <option value={m.key} key={m.key}>{m.label}</option>
              ))}
            </select>
            <input
              className="tu-input"
              type="date"
              value={reqFrom}
              onChange={(e) => setReqFrom(e.target.value)}
              title="From date"
            />
            <input
              className="tu-input"
              type="date"
              value={reqTo}
              onChange={(e) => setReqTo(e.target.value)}
              title="To date"
            />
            <button type="button" className="tu-btn tu-btn-primary" onClick={applyReqFilters}>
              Search
            </button>
            {reqFiltersActive ? (
              <button type="button" className="tu-btn tu-btn-light" onClick={clearReqFilters}>
                Clear
              </button>
            ) : null}
          </div>
        ) : null}

        {historyTab === 'transfers' ? (
          <div className="tu-filters">
            <input
              className="tu-input"
              placeholder="Search name, username or note…"
              value={xferSearch}
              onChange={(e) => setXferSearch(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && applyXferFilters()}
            />
            <input
              className="tu-input"
              type="date"
              value={xferFrom}
              onChange={(e) => setXferFrom(e.target.value)}
              title="From date"
            />
            <input
              className="tu-input"
              type="date"
              value={xferTo}
              onChange={(e) => setXferTo(e.target.value)}
              title="To date"
            />
            <button type="button" className="tu-btn tu-btn-primary" onClick={applyXferFilters}>
              Search
            </button>
            {xferFiltersActive ? (
              <button type="button" className="tu-btn tu-btn-light" onClick={clearXferFilters}>
                Clear
              </button>
            ) : null}
          </div>
        ) : null}

        {historyTab === 'requests' && history.rows.length === 0 ? (
          <div className="tu-center tu-muted" style={{ padding: '18px 0' }}>
            <div className="tu-empty-icon">
              <i className="fa fa-inbox" aria-hidden="true"></i>
            </div>
            You have not asked for a topup yet.
          </div>
        ) : historyTab === 'requests' ? (
          <div className="tu-requests">
            {history.rows.map((row) => {
              const style = STATUS_STYLE[row.status] || STATUS_STYLE.pending;
              const isOpen = open === row._id;
              return (
                <article key={row._id} className={isOpen ? 'tu-req tu-req-open' : 'tu-req'}>
                  <div className="tu-req-top">
                    <div className="tu-req-amount">
                      <i
                        className={`fa ${METHOD_ICON[row.method] || 'fa-credit-card'}`}
                        aria-hidden="true"
                      ></i>
                      <div>
                        <b>BDT {row.amountBdt.toLocaleString()}</b>
                        <small>
                          {row.method.replace('_', ' ')} ·{' '}
                          {new Date(row.createdAt).toLocaleString()}
                        </small>
                      </div>
                    </div>
                    <span
                      className="tu-chip"
                      style={{ background: style.bg, color: style.color }}
                    >
                      {style.label}
                    </span>
                  </div>

                  <div className="tu-req-bottom">
                    <span className="tu-muted">
                      {row.status === 'approved'
                        ? `৳${row.amountBdt.toLocaleString()} added to Shopping Balance`
                        : `৳${row.amountBdt.toLocaleString()} to Shopping Balance on approval`}
                    </span>
                    <span className="tu-req-actions">
                      <button className="tu-linkbtn" onClick={() => setOpen(isOpen ? '' : row._id)}>
                        {isOpen ? 'Hide' : 'Details'}
                      </button>
                      {row.status === 'pending' ? (
                        <button className="tu-linkbtn tu-danger" onClick={() => cancel(row)}>
                          Cancel
                        </button>
                      ) : null}
                    </span>
                  </div>

                  {isOpen ? (
                    <div className="tu-detail">
                      <div>
                        <b>Reference</b>
                        <span>{String(row._id).slice(-8).toUpperCase()}</span>
                      </div>
                      {row.methodDetails?.transactionId ? (
                        <div>
                          <b>Transaction</b>
                          <span>{row.methodDetails.transactionId}</span>
                        </div>
                      ) : null}
                      {row.methodDetails?.provider ? (
                        <div>
                          <b>Paid via</b>
                          <span>
                            {row.methodDetails.provider} {row.methodDetails.accountNumber}
                          </span>
                        </div>
                      ) : null}
                      {row.note ? (
                        <div>
                          <b>Your note</b>
                          <span>{row.note}</span>
                        </div>
                      ) : null}
                      {row.verifiedAt ? (
                        <div>
                          <b>Verified by Accounts</b>
                          <span>
                            {new Date(row.verifiedAt).toLocaleString()} by {row.verifiedByName}
                          </span>
                        </div>
                      ) : null}
                      {row.approvedAt ? (
                        <div>
                          <b>Approved</b>
                          <span>
                            {new Date(row.approvedAt).toLocaleString()} by {row.approvedByName}
                          </span>
                        </div>
                      ) : null}
                      {row.rejectReason ? (
                        <div className="tu-bad">
                          <b>Reason</b>
                          <span>{row.rejectReason}</span>
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                </article>
              );
            })}
          </div>
        ) : assocHistory.rows.length === 0 ? (
          <div className="tu-center tu-muted" style={{ padding: '18px 0' }}>
            <div className="tu-empty-icon">
              <i className="fa fa-handshake-o" aria-hidden="true"></i>
            </div>
            No merchant transfers yet.
          </div>
        ) : (
          <div className="tu-requests">
            {assocHistory.rows.map((t) => {
              const sent = t.fromUsername === userInfo?.username;
              const isOpen = assocOpen === t._id;
              const counterpartName = sent ? t.toName : t.fromName;
              const counterpartUsername = sent ? t.toUsername : t.fromUsername;
              const myBalanceAfter = sent ? t.fromBalanceAfter : t.toBalanceAfter;
              return (
                <article key={t._id} className={isOpen ? 'tu-req tu-req-open' : 'tu-req'}>
                  <div className="tu-req-top">
                    <div className="tu-req-amount">
                      <i
                        className={sent ? 'fa fa-arrow-up' : 'fa fa-arrow-down'}
                        aria-hidden="true"
                      ></i>
                      <div>
                        <b>
                          {sent ? 'To' : 'From'} {counterpartName || counterpartUsername}
                        </b>
                        <small>
                          Tk {Number(t.amount).toLocaleString()} ·{' '}
                          {new Date(t.createdAt).toLocaleString()}
                        </small>
                      </div>
                    </div>
                    <span
                      className="tu-chip"
                      style={
                        t.otpUsed
                          ? { background: '#dcfce7', color: '#166534' }
                          : { background: '#e5e7eb', color: '#374151' }
                      }
                    >
                      {t.otpUsed ? 'OTP verified' : 'No OTP'}
                    </span>
                  </div>

                  {!sent ? (
                    <div className="tu-merchant-flag">
                      <img src={t.associationLogo || DEFAULT_LOGO} alt="" className="tu-assoc-avatar" />
                      <div>
                        <b>{t.associationName || counterpartName || counterpartUsername}</b>
                        <small className="tu-muted">@{t.fromUsername} · Merchant</small>
                      </div>
                    </div>
                  ) : null}

                  <div className="tu-req-bottom">
                    <span className="tu-muted">
                      @{counterpartUsername}
                      {t.note ? ` · ${t.note}` : ''}
                    </span>
                    <span className="tu-req-actions">
                      <button className="tu-linkbtn" onClick={() => setAssocOpen(isOpen ? '' : t._id)}>
                        {isOpen ? 'Hide' : 'Details'}
                      </button>
                    </span>
                  </div>

                  {isOpen ? (
                    <div className="tu-detail">
                      <div>
                        <b>Reference</b>
                        <span>{String(t._id).slice(-8).toUpperCase()}</span>
                      </div>
                      <div>
                        <b>{sent ? 'Sent to' : 'Received from'}</b>
                        <span>
                          {counterpartName} (@{counterpartUsername})
                        </span>
                      </div>
                      {sent && t.associationName ? (
                        <div>
                          <b>Sent as</b>
                          <span>{t.associationName}</span>
                        </div>
                      ) : null}
                      {!sent && t.associationName ? (
                        <div>
                          <b>Merchant</b>
                          <span>
                            {t.associationName} (@{t.fromUsername})
                          </span>
                        </div>
                      ) : null}
                      <div>
                        <b>Amount</b>
                        <span>Tk {Number(t.amount).toLocaleString()}</span>
                      </div>
                      {t.note ? (
                        <div>
                          <b>Note</b>
                          <span>{t.note}</span>
                        </div>
                      ) : null}
                      <div>
                        <b>Verification</b>
                        <span>{t.otpUsed ? 'OTP verified before sending' : 'No OTP was required'}</span>
                      </div>
                      <div>
                        <b>Your balance after</b>
                        <span>{Number(myBalanceAfter || 0).toLocaleString()}</span>
                      </div>
                      <div>
                        <b>When</b>
                        <span>{new Date(t.createdAt).toLocaleString()}</span>
                      </div>
                    </div>
                  ) : null}
                </article>
              );
            })}
          </div>
        )}

        {historyTab === 'requests' && history.pages > 1 ? (
          <div className="tu-pager">
            <button
              className="tu-btn tu-btn-light"
              disabled={history.page <= 1}
              onClick={() => loadHistory(history.page - 1, reqFilters)}
            >
              ‹ Prev
            </button>
            <span className="tu-muted">
              Page {history.page} of {history.pages}
            </span>
            <button
              className="tu-btn tu-btn-light"
              disabled={history.page >= history.pages}
              onClick={() => loadHistory(history.page + 1, reqFilters)}
            >
              Next ›
            </button>
          </div>
        ) : null}

        {historyTab === 'transfers' && assocHistory.pages > 1 ? (
          <div className="tu-pager">
            <button
              className="tu-btn tu-btn-light"
              disabled={assocHistory.page <= 1}
              onClick={() => loadAssocTransfers(assocHistory.page - 1, xferFilters)}
            >
              ‹ Prev
            </button>
            <span className="tu-muted">
              Page {assocHistory.page} of {assocHistory.pages}
            </span>
            <button
              className="tu-btn tu-btn-light"
              disabled={assocHistory.page >= assocHistory.pages}
              onClick={() => loadAssocTransfers(assocHistory.page + 1, xferFilters)}
            >
              Next ›
            </button>
          </div>
        ) : null}
      </section>
    </Frame>
  );
}

const CSS = `
.tu-body { padding: 12px; display: flex; flex-direction: column; gap: 12px; }
.tu-hero { display: flex; align-items: center; justify-content: space-between; gap: 10px;
  background: linear-gradient(135deg, #42b4e7, #2f8fc4); color: #fff; border-radius: 14px;
  padding: 14px 16px; box-shadow: 0 1px 4px rgba(0,0,0,.15); }
.tu-hero-main { display: flex; flex-direction: column; line-height: 1.25; min-width: 0; }
.tu-hero-main span { font-size: 12px; opacity: .9; text-transform: uppercase; letter-spacing: .04em; }
.tu-hero-main strong { font-size: 30px; font-weight: 800; }
.tu-hero-main small { font-size: 12px; opacity: .9; }
.tu-intro { margin: 0; color: #14506e; font-size: 13.5px; line-height: 1.5; }
.tu-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; align-items: start; }
@media (max-width: 900px) { .tu-grid { grid-template-columns: 1fr; } }
.tu-panel { background: #fff; border-radius: 14px; padding: 14px; display: flex;
  flex-direction: column; gap: 8px; box-shadow: 0 1px 3px rgba(0,0,0,.12); }
.tu-panel-head { display: flex; align-items: center; gap: 8px; margin: 0 0 4px; padding-bottom: 8px;
  border-bottom: 1px solid #e5e7eb; font-size: 13px; font-weight: 800; text-transform: uppercase;
  letter-spacing: .03em; color: #14506e; }
.tu-panel-head i { color: #42b4e7; }
.tu-count { margin-left: auto; background: #e6f5fd; color: #14506e; border-radius: 999px;
  padding: 1px 9px; font-size: 12px; }
.tu-filters { display: flex; gap: 8px; flex-wrap: wrap; align-items: center;
  margin-bottom: 10px; padding: 10px; background: #f8fafc; border-radius: 10px; }
.tu-filters .tu-input { width: auto; flex: 1 1 160px; min-width: 130px; }
.tu-tabs { display: flex; gap: 6px; margin-bottom: 4px; flex-wrap: wrap; }
.tu-tab { display: inline-flex; align-items: center; gap: 7px; border: 1.5px solid #e5e7eb;
  background: #f8fafc; color: #374151; border-radius: 999px; padding: 7px 14px;
  font-size: 12.5px; font-weight: 700; cursor: pointer; transition: all .15s ease; }
.tu-tab .tu-count { margin-left: 2px; }
.tu-tab-blue { color: #14506e; }
.tu-tab-blue i { color: #42b4e7; }
.tu-tab-purple { color: #5b21b6; }
.tu-tab-purple i { color: #8b5cf6; }
.tu-tab-blue.tu-tab-active { background: linear-gradient(135deg, #42b4e7, #2f8fc4); border-color: #2f8fc4; color: #fff; }
.tu-tab-purple.tu-tab-active { background: linear-gradient(135deg, #8b5cf6, #6d28d9); border-color: #6d28d9; color: #fff; }
.tu-tab-active i { color: #fff !important; }
.tu-tab-active .tu-count { background: rgba(255,255,255,.25); color: #fff; }
.tu-tab:not(.tu-tab-active):hover { background: #eef2f7; }
.tu-sub { margin-top: 6px; font-size: 12px; font-weight: 700; color: #14506e; }
.tu-label { font-size: 12px; font-weight: 700; color: #374151; margin: 6px 0 0; }
.tu-input { width: 100%; border: 1px solid #cfd8e3; border-radius: 10px; padding: 10px 12px;
  font-size: 14px; box-sizing: border-box; background: #fff; }
.tu-input:focus { outline: 3px solid #cdeafa; border-color: #42b4e7; }
.tu-amount { font-size: 20px; font-weight: 700; letter-spacing: .01em; }
.tu-two { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
@media (max-width: 600px) { .tu-two { grid-template-columns: 1fr; } }
.tu-two > div { min-width: 0; }
.tu-methods { display: grid; grid-template-columns: repeat(auto-fit, minmax(120px, 1fr)); gap: 8px; }
.tu-method { display: flex; flex-direction: column; align-items: center; gap: 4px;
  border: 1px solid #cfd8e3; background: #fff; border-radius: 12px; padding: 10px 8px;
  font-size: 12.5px; font-weight: 700; color: #374151; cursor: pointer; transition: all .15s; }
.tu-method i { font-size: 17px; color: #42b4e7; }
.tu-method:hover { border-color: #42b4e7; }
.tu-method-on { background: #42b4e7; border-color: #42b4e7; color: #fff; }
.tu-method-on i { color: #fff; }
.tu-hint { background: #e6f5fd; border: 1px solid #bfe4f8; color: #14506e; border-radius: 10px;
  padding: 9px 11px; font-size: 12.5px; line-height: 1.5; }
.tu-preview { background: #ecfdf5; border: 1px solid #a7f3d0; color: #065f46; border-radius: 10px;
  padding: 9px 11px; font-size: 13px; }
.tu-row { display: flex; gap: 8px; align-items: center; }
.tu-btn { border-radius: 10px; padding: 10px 14px; font-weight: 700; font-size: 14px;
  cursor: pointer; border: 1px solid #cfd8e3; background: #fff; white-space: nowrap; }
.tu-btn-primary { background: #42b4e7; border-color: #42b4e7; color: #fff; }
.tu-btn-primary:hover:enabled { background: #2f9fd1; }
.tu-btn-primary:disabled { opacity: .6; cursor: not-allowed; }
.tu-btn-light { background: #f1f5f9; }
.tu-full { width: 100%; margin-top: 8px; padding: 12px; font-size: 15px; }
.tu-chip { border-radius: 999px; padding: 3px 10px; font-size: 11px; font-weight: 700;
  display: inline-block; }
.tu-chip-green { background: #dcfce7; color: #166534; white-space: nowrap; }
.tu-chip-purple { background: #ede9fe; color: #5b21b6; white-space: nowrap;
  display: inline-flex; align-items: center; gap: 6px; }
.tu-assoc-logo { width: 16px; height: 16px; border-radius: 50%; object-fit: cover; }
.tu-assoc-avatar { width: 34px; height: 34px; border-radius: 50%; object-fit: cover;
  background: #f1f5f9; flex-shrink: 0; }
.tu-assoc-picked { display: flex; align-items: center; gap: 10px; padding: 8px 10px;
  border: 1px solid #e5e7eb; border-radius: 10px; margin-bottom: 10px; }
.tu-suggest { border: 1px solid #e5e7eb; border-radius: 10px; margin-top: 6px;
  max-height: 220px; overflow-y: auto; }
.tu-suggest-row { display: flex; align-items: center; gap: 10px; width: 100%;
  padding: 8px 10px; border: none; background: #fff; border-bottom: 1px solid #f1f5f9;
  cursor: pointer; }
.tu-suggest-row:last-child { border-bottom: none; }
.tu-suggest-row:hover { background: #f8fafc; }
.tu-merchant-flag { display: flex; align-items: center; gap: 8px; background: #f5f3ff;
  border: 1px solid #ede9fe; border-radius: 10px; padding: 6px 10px; margin: 2px 0; }
.tu-merchant-flag b { font-size: 12.5px; color: #5b21b6; display: block; }
.tu-merchant-flag small { font-size: 11px; }
.tu-muted { color: #6b7280; font-size: 12px; }
.tu-center { text-align: center; }
.tu-empty-icon { font-size: 30px; color: #b6d7e8; margin-bottom: 6px; }
.tu-ok { color: #166534; font-size: 12.5px; }
.tu-bad { color: #991b1b; font-size: 12.5px; }
.tu-list { list-style: none; margin: 0; padding: 0; }
.tu-list li { display: flex; align-items: center; gap: 8px; padding: 7px 0;
  border-bottom: 1px solid #f1f5f9; font-size: 12.5px; }
.tu-list li b { margin-left: auto; }
.tu-dir-out { color: #b45309; }
.tu-dir-in { color: #166534; }
.tu-requests { display: flex; flex-direction: column; gap: 10px; }
.tu-req { border: 1px solid #e5e7eb; border-radius: 12px; padding: 10px 12px; }
.tu-req-open { border-color: #42b4e7; box-shadow: 0 0 0 3px #e6f5fd; }
.tu-req-top { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
.tu-req-amount { display: flex; align-items: center; gap: 10px; flex: 1 1 200px; min-width: 0; }
.tu-req-amount > i { width: 34px; height: 34px; flex: none; border-radius: 10px; background: #e6f5fd;
  color: #42b4e7; display: flex; align-items: center; justify-content: center; font-size: 15px; }
.tu-req-amount b { display: block; font-size: 15px; }
.tu-req-amount small { display: block; color: #6b7280; font-size: 11.5px; text-transform: capitalize; }
.tu-req-bottom { display: flex; align-items: center; justify-content: space-between; gap: 8px;
  margin-top: 8px; padding-top: 8px; border-top: 1px dashed #e5e7eb; flex-wrap: wrap; }
.tu-req-actions { display: flex; gap: 4px; }
.tu-linkbtn { background: none; border: 0; color: #2f8fc4; font-size: 12.5px; font-weight: 700;
  cursor: pointer; padding: 2px 6px; }
.tu-danger { color: #b91c1c; }
.tu-detail { margin-top: 8px; background: #f8fafc; border-radius: 10px; padding: 10px; font-size: 12.5px; }
.tu-detail > div { display: flex; gap: 8px; padding: 3px 0; }
.tu-detail b { flex: 0 0 130px; color: #6b7280; font-weight: 600; }
.tu-detail span { min-width: 0; word-break: break-word; }
@media (max-width: 480px) {
  .tu-detail > div { flex-direction: column; gap: 0; }
  .tu-detail b { flex: none; }
  .tu-hero-main strong { font-size: 24px; }
}
.tu-pager { display: flex; align-items: center; justify-content: center; gap: 10px; padding-top: 10px; }
`;
